import type { TextFieldProps } from '@mui/material';
import { TextField } from '@mui/material';
import { useDebounce } from 'hooks/helpers';
import type { ChangeEvent } from 'react';
import { forwardRef, useEffect, useState } from 'react';

const TextFieldWithDebounce = forwardRef<HTMLInputElement, TextFieldProps>(
    ({ onChange, value, ...otherProps }, ref) => {
        const [debouncedValue, setDebouncedValue] = useState<unknown>(value);
        const [isEditing, setIsEditing] = useState(false);
        const debouncedOnChange = useDebounce(
            (event: React.ChangeEvent<HTMLInputElement>) => {
                if (onChange) onChange(event);
                setIsEditing(false);
            },
            1000,
        );
        const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
            setIsEditing(true);
            setDebouncedValue(event.target.value);
            debouncedOnChange(event);
        };
        useEffect(() => {
            // if props.value has been updated by an API call while the user is editing, we don't want to override the user's value
            // note: the loose equality check is intentional, so "10" is considered equal to 10
            // eslint-disable-next-line eqeqeq
            if (value != debouncedValue && !isEditing) setDebouncedValue(value);
            // we want to run this effect only when props.value change
        }, [value]);
        return (
            <TextField
                onChange={handleChange}
                value={debouncedValue}
                {...otherProps}
                ref={ref}
                sx={{
                    ...otherProps.sx,
                    ...{
                        '& textarea': {
                            overflowY: 'hidden',
                            paddingRight: '0px',
                        },
                        '&:hover textarea': {
                            overflowY: 'scroll',
                            paddingRight: '5px',
                        },
                        //specific chrome style
                        '& textarea::-webkit-scrollbar': {
                            width: '8px',
                        },
                        '& textarea::-webkit-scrollbar-thumb': {
                            backgroundColor: 'rgba(0, 0, 0, 0.3)',
                            borderRadius: '4px',
                        },
                    },
                }}
            />
        );
    },
);

TextFieldWithDebounce.displayName = 'TextFieldWithDebounce';

export default TextFieldWithDebounce;
